import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

export type CompensationDecisionNotesLeavePayload = {
	cycleId: string;
	managerId: string;
	/** Keys are direct-report `employee_id` strings (matches employee-compensation `employee_id`); values are note text */
	notesByDecisionRowId: Record<string, string>;
};

type UseDecisionNotesAutosaveArgs = {
	notesValuesRef: React.MutableRefObject<Record<string, string>>;
	cycleId: string | undefined;
	managerId: string | undefined;
	persist: (payload: CompensationDecisionNotesLeavePayload) => void | Promise<void>;
	/** Idle time after the last edit before calling `persist` */
	debounceMs?: number;
};

const defaultDebounceMs = 800;

const hasNonEmptyNote = (notes: Record<string, string>): boolean =>
	Object.values(notes).some((v) => v.trim().length > 0);

/**
 * Hybrid autosave: debounced persist after edits, plus a final flush when the component
 * unmounts or when `cycleId` / `userId` changes (e.g. navigating to another cycle).
 */
export const useDecisionNotesAutosave = ({
	notesValuesRef,
	cycleId,
	managerId,
	persist,
	debounceMs = defaultDebounceMs,
}: UseDecisionNotesAutosaveArgs): { schedulePersist: () => void } => {
	const persistRef = useRef(persist);
	useLayoutEffect(() => {
		persistRef.current = persist;
	});

	const cycleIdRef = useRef(cycleId);
	const managerIdRef = useRef(managerId);
	useLayoutEffect(() => {
		cycleIdRef.current = cycleId;
		managerIdRef.current = managerId;
	});

	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	/** Uses closure cycleId/managerId so cleanup flushes the scope that was active for this effect. */
	const flushPendingNotesForScope = useCallback(() => {
		if (!cycleId || !managerId) return;
		const notesByDecisionRowId = { ...notesValuesRef.current };
		if (!hasNonEmptyNote(notesByDecisionRowId)) return;
		void persistRef.current({
			cycleId,
			managerId,
			notesByDecisionRowId,
		});
	}, [cycleId, managerId, notesValuesRef]);

	const schedulePersist = useCallback(() => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			const cid = cycleIdRef.current;
			const nw = managerIdRef.current;
			if (!cid || !nw) return;
			const notesByDecisionRowId = { ...notesValuesRef.current };
			if (!hasNonEmptyNote(notesByDecisionRowId)) return;
			void persistRef.current({
				cycleId: cid,
				managerId: nw,
				notesByDecisionRowId,
			});
		}, debounceMs);
	}, [debounceMs, notesValuesRef]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
			flushPendingNotesForScope();
		};
	}, [cycleId, managerId, flushPendingNotesForScope]);

	return { schedulePersist };
};
